import { useState, useCallback, useEffect, useRef } from "react";
import { Outlet, NavLink, Navigate, useLocation } from "react-router-dom";
import { useAuth } from "../../hooks/useAuth";
import useScrollRestore from "../../hooks/useScrollRestore";
import ChatWidget from "../ChatWidget/ChatWidget";
import NotificationBell from "../Notifications/NotificationBell";
import "./Layout.css";

function Layout() {
  const { isAuthenticated, isApproved, loading, user, logout } = useAuth();
  const [menuOpen, setMenuOpen] = useState(false);
  const [roboticsOpen, setRoboticsOpen] = useState(false);
  const closeMenu = useCallback(() => {
    setMenuOpen(false);
    setRoboticsOpen(false);
  }, []);
  const location = useLocation();
  const menuRef = useRef(null);
  const roboticsButtonRef = useRef(null);
  const roboticsMenuRef = useRef(null);
  // Restore scroll position of the main content area on every refresh
  // / route change. Keyed per pathname+hash so each tab keeps its own.
  const mainRef = useScrollRestore();

  // Close menu on route change
  useEffect(() => {
    setMenuOpen(false);
    setRoboticsOpen(false);
  }, [location.pathname]);

  // Close on outside click
  useEffect(() => {
    if (!menuOpen) return;
    const onClick = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [menuOpen]);

  if (loading) {
    return (
      <div className="layout-loading">
        <div className="loading-spinner" />
        <p>Loading...</p>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/auth" replace />;
  }

  if (!isApproved) {
    return (
      <div style={{ textAlign: "center", padding: "4rem", maxWidth: "480px", margin: "0 auto" }}>
        <div style={{ fontSize: "4rem", marginBottom: "1.5rem" }}>⏳</div>
        <h2 style={{ color: "var(--color-text)", marginBottom: "1rem", fontSize: "1.5rem" }}>
          Account Pending Approval
        </h2>
        <p style={{ color: "var(--color-text-muted)", lineHeight: "1.6", marginBottom: "2rem" }}>
          Your account is waiting for administrator approval.
          You'll be able to access Anna once approved.
        </p>
        <button
          onClick={logout}
          style={{
            padding: "0.75rem 1.5rem",
            borderRadius: "8px",
            border: "1px solid var(--color-border)",
            background: "var(--color-surface)",
            color: "var(--color-text)",
            cursor: "pointer",
          }}
        >
          Sign Out
        </button>
      </div>
    );
  }

  const navLinkClass = ({ isActive }) => isActive ? "active" : "";
  const roboticsActive = ["/singularity-node", "/house-map", "/weather", "/device-ota"].includes(location.pathname);

  const openRoboticsAndFocus = () => {
    setRoboticsOpen(true);
    requestAnimationFrame(() => roboticsMenuRef.current?.querySelector("a")?.focus());
  };

  const handleRoboticsKeyDown = (event) => {
    if (["ArrowRight", "ArrowDown", "Enter", " "].includes(event.key)) {
      event.preventDefault();
      openRoboticsAndFocus();
    }
  };

  const handleRoboticsMenuKeyDown = (event) => {
    if (event.key === "Escape" || event.key === "ArrowLeft") {
      event.preventDefault();
      setRoboticsOpen(false);
      roboticsButtonRef.current?.focus();
    }
  };

  return (
    <div className="layout">
      <header className="topbar">
        <div className="topbar-brand">
          <img src="/icons/anna-wordmark-crop.jpg" alt="Anna" className="brand-wordmark" />
        </div>
        <ul className="nav-links">
          <li><NavLink to="/chat" className={navLinkClass}><img src="/icons/icon-chat.png" alt="" className="nav-icon" /> Anna</NavLink></li>
        </ul>
        <div className="topbar-user">
          {user?.picture && (
            <img src={user.picture} alt="" className="user-avatar" referrerPolicy="no-referrer" />
          )}
          <span className="user-name">{user?.name || user?.email}</span>
          <NotificationBell />
          <button className="btn-secondary logout-btn" onClick={logout}>Sign Out</button>
          <div className="menu-wrapper" ref={menuRef}>
            <button
              className="hamburger-btn"
              onClick={() => setMenuOpen(o => !o)}
              aria-label="Toggle menu"
              aria-expanded={menuOpen}
            >
              <img src="/icons/icon-hamburger.png" alt="" className="hamburger-icon" />
            </button>
            {menuOpen && (
              <div className="menu-popover" role="menu">
                <ul>
                  <li><NavLink to="/workspace" className={navLinkClass} onClick={closeMenu}><img src="/icons/icon-workspace.png" alt="" className="nav-icon" /> Workspace</NavLink></li>
                  <li><NavLink to="/memories" className={navLinkClass} onClick={closeMenu}><img src="/icons/icon-memories.png" alt="" className="nav-icon" /> Memories</NavLink></li>
                  <li><NavLink to="/devices" className={navLinkClass} onClick={closeMenu}><img src="/icons/icon-devices.png" alt="" className="nav-icon" /> Devices</NavLink></li>
                  <li><NavLink to="/anna-core" className={navLinkClass} onClick={closeMenu}><img src="/icons/icon-anna-core.png" alt="" className="nav-icon" /> Anna's Core</NavLink></li>
                  <li
                    className="submenu-wrapper"
                    onMouseEnter={() => setRoboticsOpen(true)}
                    onMouseLeave={() => setRoboticsOpen(false)}
                    onBlur={(event) => {
                      if (!event.currentTarget.contains(event.relatedTarget)) setRoboticsOpen(false);
                    }}
                  >
                    <button
                      ref={roboticsButtonRef}
                      type="button"
                      className={`submenu-trigger ${roboticsActive ? "active" : ""}`}
                      aria-haspopup="menu"
                      aria-expanded={roboticsOpen}
                      aria-controls="robotics-menu"
                      onClick={() => setRoboticsOpen(true)}
                      onKeyDown={handleRoboticsKeyDown}
                    >
                      <span><img src="/icons/icon-devices.png" alt="" className="nav-icon" /> Robotics</span>
                      <span className="submenu-arrow" aria-hidden="true">›</span>
                    </button>
                    {roboticsOpen && (
                      <div
                        id="robotics-menu"
                        ref={roboticsMenuRef}
                        className="menu-popover submenu-popover"
                        role="menu"
                        aria-label="Robotics"
                        onKeyDown={handleRoboticsMenuKeyDown}
                      >
                        <ul>
                          <li><NavLink to="/singularity-node" className={navLinkClass} onClick={closeMenu} role="menuitem">Singularity</NavLink></li>
                          <li><NavLink to="/house-map" className={navLinkClass} onClick={closeMenu} role="menuitem">House Map</NavLink></li>
                          <li><NavLink to="/weather" className={navLinkClass} onClick={closeMenu} role="menuitem">Weather</NavLink></li>
                          <li><NavLink to="/device-ota" className={navLinkClass} onClick={closeMenu} role="menuitem">Device OTA</NavLink></li>
                        </ul>
                      </div>
                    )}
                  </li>
                  <li><NavLink to="/admin" className={navLinkClass} onClick={closeMenu}><img src="/icons/icon-admin.png" alt="" className="nav-icon" /> Admin</NavLink></li>
                </ul>
                <div className="menu-section">
                  <span className="menu-section-title">Agents</span>
                  <ul>
                    <li><NavLink to="/irene" className={navLinkClass} onClick={closeMenu}><img src="/icons/icon-admin.png" alt="" className="nav-icon" /> Irene</NavLink></li>
                    <li><NavLink to="/aura" className={navLinkClass} onClick={closeMenu}><img src="/icons/icon-devices.png" alt="" className="nav-icon" /> Aura</NavLink></li>
                    <li><NavLink to="/tkadlec" className={navLinkClass} onClick={closeMenu}><img src="/icons/icon-tkadlec.png" alt="" className="nav-icon" /> Tkadlec</NavLink></li>
                    <li><NavLink to="/huginn" className={navLinkClass} onClick={closeMenu}><img src="/icons/icon-huginn.png" alt="" className="nav-icon" /> Huginn</NavLink></li>
                    <li><NavLink to="/usporadatel" className={navLinkClass} onClick={closeMenu}><img src="/icons/icon-admin.png" alt="" className="nav-icon" /> Uspořadatel</NavLink></li>
                  </ul>
                </div>
              </div>
            )}
          </div>
        </div>
      </header>

      <main className="main-content" ref={mainRef}>
        <Outlet />
      </main>
      {!['/chat', '/doppelganger'].includes(location.pathname) && <ChatWidget />}
    </div>
  );
}

export default Layout;
